import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { STRIPE_PROXY_URL } from '../../config/billing';
import { stripeFetch } from '../../api/stripe';

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  defaultCustomerId?: string;
  customers: Array<{ id: number; company_name: string; stripe_customer_id?: string }>;
  onCreated?: () => void;
}

interface LineItem {
  description: string;
  amount: string; // dollars
  quantity: number;
}

export const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open, onClose, defaultCustomerId, customers, onCreated,
}) => {
  const [customerId, setCustomerId] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([{ description: '', amount: '', quantity: 1 }]);
  const [finalize, setFinalize] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCustomerId(defaultCustomerId || '');
      setLineItems([{ description: '', amount: '', quantity: 1 }]);
      setFinalize(false);
      setError('');
    }
  }, [open, defaultCustomerId]);

  const stripeCusts = customers.filter(c => c.stripe_customer_id);

  const updateItem = (idx: number, field: keyof LineItem, value: any) => {
    const next = [...lineItems];
    next[idx] = { ...next[idx], [field]: value };
    setLineItems(next);
  };

  const handleSubmit = async () => {
    if (!customerId) { setError('Select a customer'); return; }
    if (lineItems.every(li => !li.description && !li.amount)) { setError('Add at least one line item'); return; }
    setSubmitting(true);
    setError('');
    try {
      // 1. Create invoice
      const invRes = await stripeFetch(`${STRIPE_PROXY_URL}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer: customerId, auto_advance: finalize }),
      });
      if (!invRes.ok) { const e = await invRes.json(); throw new Error(e.error || 'Failed to create invoice'); }
      const invoice = await invRes.json();

      // 2. Add line items
      for (const li of lineItems) {
        if (!li.description && !li.amount) continue;
        const parsed = parseFloat(li.amount || '0');
        const cents = isNaN(parsed) || parsed < 0 ? 0 : Math.round(parsed * 100);
        await stripeFetch(`${STRIPE_PROXY_URL}/invoice-items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer: customerId,
            invoice: invoice.id,
            description: li.description,
            unit_amount: cents,
            quantity: li.quantity || 1,
          }),
        });
      }

      // 3. Optionally finalize
      if (finalize) {
        await stripeFetch(`${STRIPE_PROXY_URL}/invoices/finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoice_id: invoice.id }),
        });
      }

      onCreated?.();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Invoice" size="lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
          >
            <option value="">Select customer...</option>
            {stripeCusts.map(c => (
              <option key={c.stripe_customer_id} value={c.stripe_customer_id}>
                {c.company_name} ({c.stripe_customer_id})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Line Items</label>
          {lineItems.map((li, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Description"
                value={li.description}
                onChange={e => updateItem(idx, 'description', e.target.value)}
              />
              <div className="relative w-28">
                <span className="absolute left-3 top-2 text-gray-400 text-sm">$</span>
                <input
                  type="number" step="0.01" min="0"
                  className="w-full border border-gray-300 rounded-lg pl-7 pr-2 py-2 text-sm"
                  placeholder="0.00"
                  value={li.amount}
                  onChange={e => updateItem(idx, 'amount', e.target.value)}
                />
              </div>
              <input
                type="number" min={1}
                className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center"
                value={li.quantity}
                onChange={e => updateItem(idx, 'quantity', Number(e.target.value) || 1)}
              />
              {lineItems.length > 1 && (
                <button onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))} className="text-red-500 text-sm">✕</button>
              )}
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setLineItems([...lineItems, { description: '', amount: '', quantity: 1 }])}>+ Add Line Item</Button>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={finalize} onChange={e => setFinalize(e.target.checked)} className="rounded" />
          <span>Finalize immediately (sends to customer)</span>
        </label>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>{submitting ? 'Creating...' : finalize ? 'Create & Send Invoice' : 'Create Draft Invoice'}</Button>
        </div>
      </div>
    </Modal>
  );
};
